// ============= Imports =============

import type { CurrentUser } from '../../../../../types/permissions.types';
import type { SectionKey } from '../../../../../types/formData.types';
import {
  hasSectionAuthorization,
  normalizeAssignedSectionKeys,
} from '../../../../../types/formData.types';
import { getSpoofUserHeader } from '../../../../../utils/permissions';
import type { RejectionReason } from '../../../../../utils/rejectionUtils';
import type { RejectionDetailItem } from './types';
import {
  DEFAULT_PERMISSION_DENIED_MESSAGE,
  REJECTION_CREATOR_MESSAGE,
  REJECTION_NON_CREATOR_MESSAGE_PREFIX,
} from './constants';

export { getSpoofUserHeader };

// ============= Helpers =============

export const isPermissionRelatedError = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    message.includes('403') ||
    lower.includes('forbidden') ||
    lower.includes('unauthorized') ||
    lower.includes('permission')
  );
};

export const normalizePermissionErrorMessage = (
  message: string | undefined,
  fallback: string = DEFAULT_PERMISSION_DENIED_MESSAGE
): string => message || fallback;

export const buildRejectionBaseMessage = (
  isCurrentUserCreator: boolean,
  createdByName: unknown
): string => {
  if (isCurrentUserCreator) {
    return REJECTION_CREATOR_MESSAGE;
  }

  const creatorLabel =
    typeof createdByName === 'string' && createdByName
      ? createdByName
      : 'The creator';

  return `${REJECTION_NON_CREATOR_MESSAGE_PREFIX} ${creatorLabel} has been notified.`;
};

export const buildRejectionDetailItems = (
  formData: Record<string, unknown>,
  rejectionReason: RejectionReason | null
): RejectionDetailItem[] => {
  const detailItems: RejectionDetailItem[] = [];

  if (formData.name) {
    detailItems.push({
      label: 'Workflow',
      value: String(formData.name),
    });
  }

  if (formData.client_name) {
    detailItems.push({
      label: 'Client',
      value: String(formData.client_name),
    });
  }

  if (formData.matter_name) {
    detailItems.push({
      label: 'Matter',
      value: String(formData.matter_name),
    });
  }

  if (rejectionReason) {
    detailItems.push({
      label: 'Reason',
      value: `${rejectionReason.label}: ${rejectionReason.details}`,
    });
  }

  return detailItems;
};

export const getDependencyValues = (
  formData: Record<string, unknown> | undefined,
  dependencyFields: readonly string[]
): Record<string, unknown> =>
  dependencyFields.reduce((acc, field) => {
    acc[field] = formData?.[field];
    return acc;
  }, {} as Record<string, unknown>);

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const toLowerTrimmed = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

export const formatCommaSeparatedDisplayName = (
  displayName: string
): string => {
  const [lastName, firstName] = displayName.split(',').map((part) => part.trim());
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return displayName.trim();
};

export const isCurrentUserWorkflowCreator = (
  formData: Record<string, unknown> | undefined,
  user: CurrentUser | null | undefined
): boolean => {
  if (!formData || !user) {
    return false;
  }

  const createdBy = toLowerTrimmed(
    formData.createdByName ?? formData.createdBy
  );
  if (!createdBy) {
    return false;
  }

  const displayName = user.userDisplayName?.trim() ?? '';
  const candidates = [
    displayName,
    formatCommaSeparatedDisplayName(displayName),
    user.userEmail,
  ]
    .map(toLowerTrimmed)
    .filter(Boolean);

  return candidates.includes(createdBy);
};

const getCurrentUserIdentityValues = (
  user: CurrentUser | null | undefined
): string[] => {
  if (!user) {
    return [];
  }

  const userUpn =
    'userUpn' in user && typeof user.userUpn === 'string' ? user.userUpn : '';

  return [
    user.userEmail,
    user.userDisplayName,
    formatCommaSeparatedDisplayName(user.userDisplayName ?? ''),
    userUpn,
  ]
    .map(toLowerTrimmed)
    .filter(Boolean);
};

const extractAssignedSectionsFromInvolvedUsers = (
  involvedUsers: unknown,
  user: CurrentUser | null | undefined
): string[] => {
  if (!Array.isArray(involvedUsers) || !user) {
    return [];
  }

  const identities = new Set(getCurrentUserIdentityValues(user));

  for (const involvedUser of involvedUsers) {
    if (!isRecord(involvedUser)) {
      continue;
    }

    const involvedIdentities = [
      involvedUser.email,
      involvedUser.displayName,
      involvedUser.userId,
      involvedUser.userUpn,
    ].map(toLowerTrimmed);

    const isMatch = involvedIdentities.some((identity) =>
      identities.has(identity)
    );
    if (!isMatch || !Array.isArray(involvedUser.assignedSections)) {
      continue;
    }

    return involvedUser.assignedSections.filter(
      (section): section is string => typeof section === 'string'
    );
  }

  return [];
};

export const extractUserAssignedSections = (
  involvementData: unknown,
  user: CurrentUser | null | undefined
): string[] => {
  if (!isRecord(involvementData)) {
    return [];
  }

  if (Array.isArray(involvementData.userAssignedSections)) {
    return involvementData.userAssignedSections.filter(
      (section): section is string => typeof section === 'string'
    );
  }

  return extractAssignedSectionsFromInvolvedUsers(
    involvementData.involvedUsers,
    user
  );
};

export const isUserAssignedToSection = (
  assignedSections: string[],
  sectionKey: SectionKey
): boolean => hasSectionAuthorization(assignedSections, sectionKey);

export const toFormAssignedSectionKeys = (
  assignedSections: string[]
): string[] => normalizeAssignedSectionKeys(assignedSections);
